// One place that maps every status/priority enum to a badge colour, so the same status looks
// the same on every page.
import {
  AgreementStatus,
  CandidateStage,
  ConfigurationStatus,
  EmployeeStatus,
  ExpenseStatus,
  InvoiceStatus,
  LeaveStatus,
  ObjectiveStatus,
  OrganizationType,
  ProjectStatus,
  QuoteStatus,
  ReviewStatus,
  TaskStatus,
  TicketPriority,
  TicketStatus,
  TimeEntryStatus,
  TimesheetStatus,
} from '@denwen-msp/shared';
import { Badge, type Tone } from './display';

type Labels<T extends string> = { label: (v: T) => string };

function makeBadge<T extends string>(labels: Labels<T>, tones: Record<T, Tone>) {
  return function EnumBadge({ value, className }: { value: T; className?: string }) {
    return (
      <Badge tone={tones[value]} className={className} dot>
        {labels.label(value)}
      </Badge>
    );
  };
}

export const TicketStatusBadge = makeBadge(TicketStatus, {
  new: 'info',
  open: 'accent',
  in_progress: 'accent',
  waiting_customer: 'warning',
  waiting_third_party: 'warning',
  resolved: 'success',
  closed: 'neutral',
});
export const TicketPriorityBadge = makeBadge(TicketPriority, { critical: 'danger', high: 'warning', medium: 'info', low: 'neutral' });
export const OrganizationTypeBadge = makeBadge(OrganizationType, { customer: 'accent', prospect: 'info', vendor: 'neutral', partner: 'success', internal: 'neutral' });
export const ConfigurationStatusBadge = makeBadge(ConfigurationStatus, { active: 'success', inactive: 'neutral', in_repair: 'warning', retired: 'neutral' });
export const AgreementStatusBadge = makeBadge(AgreementStatus, { draft: 'neutral', active: 'success', expired: 'warning', cancelled: 'danger' });
export const InvoiceStatusBadge = makeBadge(InvoiceStatus, { draft: 'neutral', sent: 'info', partially_paid: 'warning', paid: 'success', void: 'neutral' });
export const QuoteStatusBadge = makeBadge(QuoteStatus, { draft: 'neutral', sent: 'info', accepted: 'success', declined: 'danger', expired: 'warning' });
export const ExpenseStatusBadge = makeBadge(ExpenseStatus, { draft: 'neutral', submitted: 'info', approved: 'success', rejected: 'danger', reimbursed: 'success' });
export const TimeEntryStatusBadge = makeBadge(TimeEntryStatus, { draft: 'neutral', submitted: 'info', approved: 'success', billed: 'accent' });
export const ProjectStatusBadge = makeBadge(ProjectStatus, { planning: 'info', active: 'accent', on_hold: 'warning', completed: 'success', cancelled: 'neutral' });
export const TaskStatusBadge = makeBadge(TaskStatus, { todo: 'neutral', in_progress: 'accent', review: 'warning', done: 'success' });
export const EmployeeStatusBadge = makeBadge(EmployeeStatus, { onboarding: 'info', active: 'success', on_leave: 'warning', offboarding: 'warning', terminated: 'neutral' });
export const LeaveStatusBadge = makeBadge(LeaveStatus, { pending: 'warning', approved: 'success', declined: 'danger', cancelled: 'neutral' });
export const TimesheetStatusBadge = makeBadge(TimesheetStatus, { draft: 'neutral', submitted: 'info', approved: 'success', rejected: 'danger' });
export const ReviewStatusBadge = makeBadge(ReviewStatus, { draft: 'neutral', self_review: 'info', manager_review: 'warning', completed: 'success' });
export const ObjectiveStatusBadge = makeBadge(ObjectiveStatus, { on_track: 'success', at_risk: 'warning', behind: 'danger', completed: 'accent' });
export const CandidateStageBadge = makeBadge(CandidateStage, { applied: 'neutral', screening: 'info', interview: 'accent', offer: 'warning', hired: 'success', rejected: 'danger' });
